import React from "react";
import {autobind} from "core-decorators";
import {action, computed, observable} from "mobx";
import {inject, observer} from "mobx-react";
import {Button, Space, Table, Typography} from "antd";
import {IdcardOutlined, ShoppingCartOutlined, TeamOutlined} from "@ant-design/icons";
import {Link} from "react-router-dom";
import _capitalize from "lodash/capitalize";
import _sortBy from "lodash/sortBy";

import "./Ensemble.scss";
import {Plans} from "@/shared/plans";
import {EnsembleUsersQuery, EnsembleUsersQueryVariables} from "@/graphql/types";
import {isInvitation, UserOrInvitation} from "./common";
import {paths} from "@/routes";
import EnsembleName from "./EnsembleName";
import EnsembleUsersNode from "./EnsembleUsers.gql";
import InvitationActions from "./InvitationActions";
import InviteUserModal from "./InviteUserModal";
import PageLayout from "@/components/PageLayout/PageLayout";
import Store from "@/stores";
import UserActions from "./UserActions";
import UserRole from "./UserRole";

export interface IEnsembleProps {
  store?: Store;
}

@inject("store")
@autobind
@observer
export default class Ensemble extends React.Component<IEnsembleProps> {
  @observable inviteUserModalOpen: boolean = false;
  @observable ownerTotalMembers: number = 0; // of all ensembles of the same owner
  @observable userEnsembles?: EnsembleUsersQuery["user_ensembles"];
  @observable userInvitations?: EnsembleUsersQuery["user_invitations"];

  async componentDidMount() {
    await this.fetchUsers();
  }

  async closeInviteUserModal() {
    this.setInviteUserModalOpen(false);
    await this.fetchUsers(); // refresh users to find new invitations
  }

  async fetchUsers() {
    const store = this.props.store!;
    const selectedEnsemble = store.session.selectedEnsemble!;
    // https://www.apollographql.com/docs/react/api/apollo-client/#ApolloClient.query
    const result = await store.apollo.client.query<EnsembleUsersQuery, EnsembleUsersQueryVariables>({
      query: EnsembleUsersNode,
      variables: {ensembleId: selectedEnsemble.id, ownerId: selectedEnsemble.owner_id},
    });
    const {user_ensembles, user_invitations, owner_user_ensembles, owner_user_invitations} = result.data!;
    this.setUserEnsembles(user_ensembles);
    this.setUserInvitations(user_invitations);
    const totalUsers = owner_user_ensembles?.aggregate?.count || 0;
    const totalInvitations = owner_user_invitations?.aggregate?.count || 0;
    this.setOwnerTotalMembers(totalUsers + totalInvitations);
  }

  // Number of invitations left (only applies to Ensemble plan subscribers)
  @computed({keepAlive: true})
  get invitationsNumber(): number {
    const extraSingers =
      this.props.store!.session.selectedEnsemble?.owner?.active_subscriptions?.[0].extra_singers || 0;
    return Plans.Ensemble.includedMembers + extraSingers - this.ownerTotalMembers;
  }

  // Number of members of this ensemble
  @computed({keepAlive: true})
  get membersNumber(): number {
    return this.userAndInvitations?.length ?? 0;
  }

  openInviteUserModal() {
    this.setInviteUserModalOpen(true);
  }

  @action
  setInviteUserModalOpen(open: boolean) {
    this.inviteUserModalOpen = open;
  }

  @action
  setOwnerTotalMembers(totalMembers: number) {
    this.ownerTotalMembers = totalMembers;
  }

  @action
  setUserEnsembles(userEnsembles: EnsembleUsersQuery["user_ensembles"]) {
    this.userEnsembles = userEnsembles;
  }

  @action
  setUserInvitations(userInvitations: EnsembleUsersQuery["user_invitations"]) {
    this.userInvitations = userInvitations;
  }

  @computed({keepAlive: true})
  get userAndInvitations(): UserOrInvitation[] {
    const userAndInvitations = [...(this.userEnsembles || []), ...(this.userInvitations || [])];
    // Sort by email
    return _sortBy(userAndInvitations, (u) => (isInvitation(u) ? u.user_email : u.user.email));
  }

  public render() {
    const {session} = this.props.store!;
    return (
      <PageLayout>
        <div className="Ensemble">
          <Typography.Title level={3}>
            <TeamOutlined className="title-icon" />
            Ensemble
          </Typography.Title>
          <Typography.Title level={5}>Name</Typography.Title>
          <EnsembleName />
          <Typography.Title level={5} style={{marginTop: "1em"}}>
            Members
          </Typography.Title>
          <Typography.Paragraph>
            Total members: {this.membersNumber}
            {session.ownerHasPlanEnsemble && (
              <>
                <br />
                Invitations left: {this.invitationsNumber}
              </>
            )}
          </Typography.Paragraph>
          <Space>
            {(session.ownerHasPlanEnsemble || session.ownerHasPlanPro) && (
              <Button
                className="add-user"
                type="primary"
                icon={<IdcardOutlined />}
                size="small"
                onClick={this.openInviteUserModal}
                disabled={session.ownerHasPlanEnsemble && this.invitationsNumber < 1}
              >
                Invite new member
              </Button>
            )}
            {session.isEnsembleOwner && session.hasPlanEnsemble && (
              <Link className="logo" to={paths.subscription()}>
                <Button className="add-invitation" type="primary" icon={<ShoppingCartOutlined />} size="small">
                  Buy more invitations
                </Button>
              </Link>
            )}
          </Space>
          <InviteUserModal open={this.inviteUserModalOpen} onClose={this.closeInviteUserModal} />
          <Table<UserOrInvitation>
            className="users-table"
            dataSource={this.userAndInvitations}
            rowKey="id"
            loading={!this.userAndInvitations}
            pagination={false}
            scroll={{x: true}}
          >
            <Table.Column<UserOrInvitation>
              title="Actions"
              key="actions"
              render={(_, user) =>
                isInvitation(user) ? (
                  <InvitationActions userInvitation={user} userInvitations={this.userInvitations!} />
                ) : (
                  <UserActions userEnsemble={user} userEnsembles={this.userEnsembles!} />
                )
              }
            />
            <Table.Column<UserOrInvitation>
              title="Email"
              render={(_, user) => (isInvitation(user) ? user.user_email : user.user.email)}
            />
            <Table.Column<UserOrInvitation> title="Name" render={(_, user) => user?.user?.name ?? ""} />
            <Table.Column<UserOrInvitation>
              title="Role"
              key="role"
              render={(_, user) => (isInvitation(user) ? _capitalize(user.role) : <UserRole userEnsemble={user} />)}
            />
            <Table.Column<UserOrInvitation>
              title="Status"
              render={(_, user) => (isInvitation(user) ? "Invited" : "Active")}
            />
          </Table>
        </div>
      </PageLayout>
    );
  }
}
